import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowSquareOut, Bug, CaretDown, CaretRight, Lightbulb, ListChecks, MagnifyingGlass, Paperclip, Sparkle, Star, Wrench, X } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { DotLoader } from '@/components/ui/DotLoader'
import { cn } from '@/lib/utils'
import { adminSubmissionsApi } from '../adminApi'
import { formatDate } from '../lib/labels'
import {
  DEFAULT_STATUS_FILTERS,
  type StatusFilters,
  adminStatusLabel,
  detailPath,
  kindLabel,
  pageCount,
  searchFromStatusFilters,
  statusFiltersFromSearch,
  toStatusListParams,
} from '../lib/statusFeed'
import {
  BUG_STATUS_TONE,
  Badge,
  EmptyState,
  Eyebrow,
  FilterPill,
  IMPORTANCE_TONE,
  PageHeader,
  Panel,
  SEVERITY_TONE,
  SUGGESTION_STATUS_TONE,
  type Tone,
  bodyStyle,
  metaStyle,
  rowClass,
  tbodyClass,
  tdClass,
  thClass,
  thStyle,
} from '../components/ui'
import type { SubmissionItem } from '../types'

const KIND_ICON: Record<string, { icon: typeof Bug; tone: Tone }> = {
  bug: { icon: Bug, tone: 'danger' },
  improve_existing: { icon: Wrench, tone: 'info' },
  new_feature: { icon: Sparkle, tone: 'violet' },
  rating: { icon: Star, tone: 'warn' },
}

function kindBadge(item: SubmissionItem) {
  const key = item.type === 'feedback' ? item.subtype ?? 'improve_existing' : item.type
  const { icon: Icon, tone } = KIND_ICON[key] ?? { icon: Lightbulb, tone: 'neutral' as Tone }
  return (
    <Badge tone={tone}>
      <Icon size={11} weight="bold" />
      {kindLabel(item)}
    </Badge>
  )
}

function statusBadge(item: SubmissionItem) {
  if (item.type === 'rating' || !item.admin_status) return <span className="text-muted-foreground dark:text-[#8a8f98]">—</span>
  const tone = item.type === 'bug' ? BUG_STATUS_TONE[item.admin_status] : SUGGESTION_STATUS_TONE[item.admin_status]
  return <Badge tone={tone ?? 'neutral'}>{adminStatusLabel(item)}</Badge>
}

function Stars({ value }: { value: number }) {
  return (
    <span className="inline-flex gap-px" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={12} weight={n <= value ? 'fill' : 'regular'} className={n <= value ? 'text-amber-500' : 'text-muted-foreground/30'} />
      ))}
    </span>
  )
}

function Detail({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-1 whitespace-pre-wrap break-words text-foreground dark:text-[#f7f8f8]" style={bodyStyle}>
        {children}
      </div>
    </div>
  )
}

const COLUMNS = ['Type', 'Reference', 'Summary', 'Module', 'Status', 'User sees', 'Reporter', 'Date']

export default function StatusPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => statusFiltersFromSearch(searchParams), [searchParams])
  const [q, setQ] = useState(filters.q)
  const [open, setOpen] = useState<number | null>(null)

  useEffect(() => {
    setQ(filters.q)
  }, [filters.q])

  const apply = (next: StatusFilters) => setSearchParams(searchFromStatusFilters(next), { replace: true })
  const set = (patch: Partial<StatusFilters>) => apply({ ...filters, ...patch, page: 1 })

  const feed = useQuery({
    queryKey: ['admin', 'submissions', toStatusListParams(filters)],
    queryFn: () => adminSubmissionsApi.list(toStatusListParams(filters)).then((r) => r.data),
    keepPreviousData: true,
  })

  const counts = feed.data?.counts ?? {}
  const allCount = (counts.bug ?? 0) + (counts.feedback ?? 0) + (counts.rating ?? 0)
  const total = feed.data?.total ?? 0
  const pages = pageCount(total)
  const hasFilters = Boolean(filters.type || filters.q)

  return (
    <div className="space-y-5">
      <PageHeader
        title="Status"
        description="Everything users have sent, newest first: bugs, suggestions and ratings, with where each one stands."
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by type">
          <FilterPill active={filters.type === ''} onClick={() => set({ type: '' })} count={allCount}>
            All
          </FilterPill>
          <FilterPill active={filters.type === 'bug'} onClick={() => set({ type: 'bug' })} count={counts.bug ?? 0}>
            Bugs
          </FilterPill>
          <FilterPill active={filters.type === 'feedback'} onClick={() => set({ type: 'feedback' })} count={counts.feedback ?? 0}>
            Suggestions
          </FilterPill>
          <FilterPill active={filters.type === 'rating'} onClick={() => set({ type: 'rating' })} count={counts.rating ?? 0}>
            Ratings
          </FilterPill>
        </div>
        <form
          className="ml-auto flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            set({ q: q.trim() })
          }}
        >
          <Input
            aria-label="Search"
            placeholder="Search reference, text or reporter"
            icon={<MagnifyingGlass size={15} />}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onBlur={() => q.trim() !== filters.q && set({ q: q.trim() })}
            className="w-64"
          />
          {hasFilters && (
            <Button type="button" variant="ghost" size="sm" onClick={() => apply(DEFAULT_STATUS_FILTERS)} icon={<X size={14} />}>
              Clear
            </Button>
          )}
        </form>
      </div>

      <Panel className="overflow-x-auto">
        <table className="w-full" style={bodyStyle}>
          <thead>
            <tr className="border-b border-border/50 dark:border-white/[0.06]">
              <th className={cn(thClass, 'w-8')} aria-label="Expand" />
              {COLUMNS.map((c) => (
                <th key={c} className={thClass} style={thStyle}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={tbodyClass}>
            {feed.isLoading && (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="py-14 text-center">
                  <DotLoader />
                </td>
              </tr>
            )}
            {feed.isError && (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="py-14 text-center text-red-700 dark:text-red-300">
                  Could not load submissions.{' '}
                  <button type="button" className="underline" onClick={() => feed.refetch()}>
                    Retry
                  </button>
                </td>
              </tr>
            )}
            {feed.data && feed.data.items.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length + 1}>
                  <EmptyState icon={<ListChecks size={26} />}>{hasFilters ? 'Nothing matches these filters.' : 'No submissions yet.'}</EmptyState>
                </td>
              </tr>
            )}
            {feed.data?.items.map((item) => {
              const isOpen = open === item.id
              const link = detailPath(item)
              const ctx = (item.context ?? {}) as Record<string, unknown>
              return (
                <Fragment key={item.id}>
                  <tr
                    className={cn(rowClass, 'cursor-pointer', isOpen && 'bg-muted/30 dark:bg-white/[0.02]')}
                    onClick={() => setOpen(isOpen ? null : item.id)}
                    aria-expanded={isOpen}
                  >
                    <td className={cn(tdClass, 'text-muted-foreground/70')}>{isOpen ? <CaretDown size={13} /> : <CaretRight size={13} />}</td>
                    <td className={tdClass}>{kindBadge(item)}</td>
                    <td className={cn(tdClass, 'font-mono whitespace-nowrap')} style={metaStyle}>
                      {item.reference_code ?? <span className="text-muted-foreground dark:text-[#8a8f98]">#{item.id}</span>}
                    </td>
                    <td className={tdClass}>
                      <div className="flex items-center gap-2 max-w-[380px]">
                        {item.rating != null && <Stars value={item.rating} />}
                        <span className="truncate" title={item.title}>
                          {item.title}
                        </span>
                        {item.attachment_url && <Paperclip size={12} className="shrink-0 text-muted-foreground" />}
                      </div>
                    </td>
                    <td className={tdClass}>{item.module ?? '—'}</td>
                    <td className={tdClass}>{statusBadge(item)}</td>
                    <td className={cn(tdClass, 'text-muted-foreground dark:text-[#8a8f98] whitespace-nowrap')} style={metaStyle}>
                      {item.user_facing_status}
                    </td>
                    <td className={tdClass}>
                      <div className="truncate max-w-[200px]" title={item.reporter.email ?? ''}>
                        {item.reporter.email ?? `user ${item.reporter.user_id}`}
                      </div>
                      {item.reporter.account_name && (
                        <div className="text-muted-foreground dark:text-[#8a8f98] truncate max-w-[200px]" style={metaStyle}>
                          {item.reporter.account_name}
                        </div>
                      )}
                    </td>
                    <td className={cn(tdClass, 'text-muted-foreground dark:text-[#8a8f98] whitespace-nowrap')} style={metaStyle}>
                      {formatDate(item.created_at)}
                    </td>
                  </tr>

                  {isOpen && (
                    <tr className="bg-muted/30 dark:bg-white/[0.02]">
                      <td />
                      <td colSpan={COLUMNS.length} className="px-3 pb-4 pt-1">
                        <div className="rounded-md border border-border/50 dark:border-white/[0.06] bg-background dark:bg-white/[0.02] p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                          <div className="lg:col-span-2 space-y-3">
                            <Detail label={item.type === 'rating' ? 'Reason' : 'Description'}>{item.description || '—'}</Detail>
                            {item.steps_to_reproduce && <Detail label="Steps to reproduce">{item.steps_to_reproduce}</Detail>}
                            {item.problem_solved && <Detail label="Problem it solves">{item.problem_solved}</Detail>}
                            {item.internal_notes && <Detail label="Internal note (team only)">{item.internal_notes}</Detail>}
                          </div>
                          <div className="space-y-3">
                            <div className="flex flex-wrap gap-1.5">
                              {item.severity && <Badge tone={SEVERITY_TONE[item.severity] ?? 'neutral'}>{item.severity}</Badge>}
                              {item.importance && <Badge tone={IMPORTANCE_TONE[item.importance] ?? 'neutral'}>{item.importance}</Badge>}
                              {item.assignee && <Badge tone="brand">{item.assignee}</Badge>}
                              {item.activity_count > 0 && <Badge>{item.activity_count} change{item.activity_count === 1 ? '' : 's'}</Badge>}
                            </div>
                            <Detail label="Reporter">
                              {item.reporter.email ?? `user ${item.reporter.user_id}`}
                              {item.reporter.full_name && <span className="text-muted-foreground dark:text-[#8a8f98]"> · {item.reporter.full_name}</span>}
                              {item.reporter.account_name && <div className="text-muted-foreground dark:text-[#8a8f98]">{item.reporter.account_name}</div>}
                            </Detail>
                            {(ctx.url != null || ctx.app_version != null) && (
                              <Detail label="Context">
                                {ctx.url != null && <div>Page {String(ctx.url)}</div>}
                                {ctx.app_version != null && <div>Version {String(ctx.app_version)}</div>}
                                {ctx.user_agent != null && (
                                  <div className="text-muted-foreground dark:text-[#8a8f98] break-all" style={metaStyle}>
                                    {String(ctx.user_agent)}
                                  </div>
                                )}
                              </Detail>
                            )}
                            <Detail label="Timeline">
                              <div>Submitted {formatDate(item.created_at)}</div>
                              {item.updated_at && item.updated_at !== item.created_at && <div>Updated {formatDate(item.updated_at)}</div>}
                              {item.resolved_at && <div>Resolved {formatDate(item.resolved_at)}</div>}
                            </Detail>
                            <div className="flex flex-wrap gap-3 pt-1" style={metaStyle}>
                              {item.attachment_url && (
                                <a href={item.attachment_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline">
                                  <Paperclip size={12} /> Attachment
                                </a>
                              )}
                              {link && (
                                <Link to={link} className="inline-flex items-center gap-1 text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline">
                                  <ArrowSquareOut size={12} /> {item.type === 'bug' ? 'Open bug' : 'Open in Suggestions'}
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </Panel>

      {pages > 1 && (
        <div className="flex items-center justify-between" style={bodyStyle}>
          <span className="text-muted-foreground dark:text-[#8a8f98]">
            Page {filters.page} of {pages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={filters.page <= 1} onClick={() => apply({ ...filters, page: filters.page - 1 })}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={filters.page >= pages} onClick={() => apply({ ...filters, page: filters.page + 1 })}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
